import { useCallback, useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { api } from "../api/apiClient";
import type { Location, Role, WebOrderAttentionCounts } from "../api/types";
import { NavSection } from "../components/NavSection";
import { NavItem } from "../components/nav/NavItem";
import type { NavIconName } from "../components/nav/NavIcon";

/**
 * The roles that get a MINIMAL shell instead of the CRM: their own pages plus
 * Profile. Server-side, CourierScopeGuard blocks every other endpoint for
 * these roles regardless of the UI. ONE shell, configured per role — never a
 * second copy of the JSX ("do not fork a surface"). G-103 S1 gives housekeeping
 * Profile only; S4 adds "My work" (schedule, hours, pay).
 */
type MinimalShell = {
  tagline: string;
  homePath: string;
  homeAriaLabel: string;
  allowedPrefixes: readonly string[];
  items: readonly { to: string; icon: NavIconName; label: string }[];
};
const MINIMAL_SHELLS: Partial<Record<Role, MinimalShell>> = {
  COURIER: {
    tagline: "Courier",
    homePath: "/app/courier/deliveries",
    homeAriaLabel: "Flower World — my deliveries",
    allowedPrefixes: ["/app/courier", "/app/profile"],
    items: [
      { to: "/app/courier/deliveries", icon: "truck", label: "My deliveries" },
      { to: "/app/courier/calendar", icon: "calendar", label: "My calendar" },
      { to: "/app/profile", icon: "person", label: "Profile" },
    ],
  },
  HOUSEKEEPING: {
    tagline: "Housekeeping",
    homePath: "/app/profile",
    homeAriaLabel: "Flower World — my profile",
    allowedPrefixes: ["/app/profile"],
    items: [{ to: "/app/profile", icon: "person", label: "Profile" }],
  },
};
import { NotificationBell } from "../components/NotificationBell";
import { useAuth } from "../state/AuthContext";
import { EmailConfirmBanner } from "../components/EmailConfirmBanner";
import { kioskAllowedOnThisDevice } from "../components/kioskAccess";
import { isNativeShell } from "../platform/shell";
import type { DashboardOutletContext } from "./dashboardOutletContext";

const MOBILE_NAV_MQ = "(max-width: 900px)";

/**
 * When false, sidebar hides Sales, Yield insights, Suppliers, and Bouquet templates
 * during Phase 1 inventory focus. Routes still exist — see README "Sidebar navigation".
 */
const SHOW_EXTENDED_BUSINESS_SIDEBAR_NAV = true;

/** When false, sidebar hides Build bouquet for Owner/Manager/Florist. Route unchanged — README. */
const SHOW_BUILD_BOUQUET_SIDEBAR_LINK = false;

export function DashboardLayout() {
  const { user, hasRole, notice } = useAuth();
  // G-133 / OD-168: kiosk mode lives in the tablet app; browsers get no link to it.
  const kioskOnThisDevice = kioskAllowedOnThisDevice(isNativeShell(), import.meta.env.DEV);
  const location = useLocation();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(false);
  // G-160 (OD-263): one small poll feeds the Orders, Web orders and Products
  // badges (it replaced reading the whole order list every 10 s).
  const [webOrderCounts, setWebOrderCounts] = useState<WebOrderAttentionCounts | null>(null);

  const minimalShell = user ? (MINIMAL_SHELLS[user.role] ?? null) : null;

  const refreshLocations = useCallback(async () => {
    // Scoped roles (courier, housekeeping) are blocked from staff endpoints;
    // don't even ask.
    if (minimalShell) return;
    setLoadError(null);
    try {
      const list = await api.listLocations();
      setLocations(list);
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : "Could not load locations");
    }
  }, [minimalShell]);

  useEffect(() => {
    void refreshLocations();
  }, [refreshLocations]);

  const refreshShopifyNotifications = useCallback(async () => {
    if (!hasRole("OWNER", "MANAGER")) {
      setWebOrderCounts(null);
      return;
    }
    try {
      setWebOrderCounts(await api.getWebOrderAttentionCounts());
    } catch {
      setWebOrderCounts(null);
    }
  }, [hasRole]);

  useEffect(() => {
    void refreshShopifyNotifications();
    const timer = window.setInterval(() => void refreshShopifyNotifications(), 10000);
    return () => window.clearInterval(timer);
  }, [refreshShopifyNotifications]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_NAV_MQ);
    const apply = () => {
      setIsMobileNav(mq.matches);
      if (!mq.matches) setMobileNavOpen(false);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.title = "Flower World";
  }, []);

  const outletCtx: DashboardOutletContext = { locations, refreshLocations };

  const openMobileNav = () => setMobileNavOpen(true);
  const closeMobileNav = () => setMobileNavOpen(false);

  const showCreateItem = hasRole("OWNER", "MANAGER");

  if (minimalShell) {
    if (
      !minimalShell.allowedPrefixes.some((path) => location.pathname.startsWith(path))
    ) {
      return <Navigate to={minimalShell.homePath} replace />;
    }
    return (
      <div
        className={
          isMobileNav && mobileNavOpen ? "app-shell app-shell--nav-open" : "app-shell"
        }
      >
        {isMobileNav && mobileNavOpen ? (
          <button
            type="button"
            className="app-nav-backdrop"
            aria-label="Close menu"
            onClick={closeMobileNav}
          />
        ) : null}

        <aside
          className="app-sidebar"
          id="app-sidebar"
          aria-hidden={isMobileNav ? !mobileNavOpen : false}
        >
          <div className="app-sidebar-brand">
            <img src="/fw-stacked-rose.svg" alt="Flower World" decoding="async" className="brand-logo-light" />
            <img src="/fw-stacked-ondark.svg" alt="" decoding="async" className="brand-logo-dark" aria-hidden="true" />
            <p className="app-sidebar-tagline">{minimalShell.tagline}</p>
          </div>
          <nav className="app-sidebar-nav" aria-label="Main">
            <NavSection title={minimalShell.tagline}>
              {minimalShell.items.map((item) => (
                <NavItem key={item.to} to={item.to} icon={item.icon} label={item.label} />
              ))}
            </NavSection>
          </nav>
        </aside>
        <main className="app-content">
          {isMobileNav ? (
            <div className="app-mobile-bar">
              <div className="app-mobile-bar-start">
                <button
                  type="button"
                  className="icon-button app-nav-toggle"
                  aria-expanded={mobileNavOpen}
                  aria-controls="app-sidebar"
                  aria-label="Menu"
                  onClick={mobileNavOpen ? closeMobileNav : openMobileNav}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
              <NavLink
                to={minimalShell.homePath}
                className={({ isActive }) =>
                  isActive ? "app-mobile-bar-brand is-active" : "app-mobile-bar-brand"
                }
                aria-label={minimalShell.homeAriaLabel}
              >
                <img src="/fw-stacked-rose.svg" alt="Flower World" decoding="async" className="brand-logo-light" />
                <img src="/fw-stacked-ondark.svg" alt="" decoding="async" className="brand-logo-dark" aria-hidden="true" />
              </NavLink>
              <div className="app-mobile-bar-end">
                <NotificationBell />
              </div>
            </div>
          ) : null}

          {!isMobileNav ? <NotificationBell /> : null}
          {notice ? <div className="alert success">{notice}</div> : null}
          <EmailConfirmBanner />
          <Outlet context={outletCtx} />
        </main>
      </div>
    );
  }

  return (
    <div
      className={
        isMobileNav && mobileNavOpen ? "app-shell app-shell--nav-open" : "app-shell"
      }
    >
      {isMobileNav && mobileNavOpen ? (
        <button
          type="button"
          className="app-nav-backdrop"
          aria-label="Close menu"
          onClick={closeMobileNav}
        />
      ) : null}

      <aside
        className="app-sidebar"
        id="app-sidebar"
        aria-hidden={isMobileNav ? !mobileNavOpen : false}
      >
        <NavLink
          to="/app"
          end
          className={({ isActive }) =>
            isActive ? "app-sidebar-brand is-active" : "app-sidebar-brand"
          }
          aria-label="Flower World — home"
        >
          <img src="/fw-stacked-rose.svg" alt="Flower World" decoding="async" className="brand-logo-light" />
          <img src="/fw-stacked-ondark.svg" alt="" decoding="async" className="brand-logo-dark" aria-hidden="true" />
        </NavLink>

        {loadError ? <div className="alert error sidebar-alert">{loadError}</div> : null}

        <nav className="app-sidebar-nav" aria-label="Main">
          {(kioskOnThisDevice && hasRole("OWNER", "ADMIN", "MANAGER", "FLORIST")) ||
          hasRole("OWNER", "ADMIN") ? (
            <NavSection title="Shop floor">
              {/* G-133 / OD-168: only the tablet app can open the kiosk, so a
                  browser never shows a link that leads to a refusal. */}
              {kioskOnThisDevice ? (
                <NavItem to="/app/in-the-shop" icon="shop" label="Kiosk Mode" />
              ) : null}
              {/* G-64. The gate here must MATCH the route's RoleGate exactly —
                  it was OWNER/MANAGER while the route allowed OWNER/ADMIN, so
                  the one role the owner actually asked for could not find the
                  page, and a manager got the silent-redirect dead button this
                  repo already recorded once for OrderNewPage. The printer is a
                  shop-floor concern, so it belongs beside the kiosk link. */}
              {hasRole("OWNER", "ADMIN") ? (
                <NavItem to="/app/printer" icon="receipt" label="Receipts" />
              ) : null}
            </NavSection>
          ) : null}

          {/* G-143 (OD-222): the menu by job. Each item keeps the exact role
              gate it had under the old Daily / Marketing / Catalog & team
              headings, so every role sees the same pages as before; a section
              is gated so a role with none of its pages never sees an empty
              heading. */}
          {hasRole("OWNER", "MANAGER", "FLORIST") ? (
            <NavSection title="Orders">
              <NavItem
                to="/app/orders"
                icon="bag"
                label="Orders"
                badges={
                  webOrderCounts
                    ? [
                        { count: webOrderCounts.blocked, tone: "danger", label: "web orders blocked" },
                        { count: webOrderCounts.soon, tone: "warning", label: "web orders need attention soon" },
                      ]
                    : undefined
                }
              />
              {hasRole("OWNER", "MANAGER") ? (
                <NavItem
                  to="/app/shopify-orders"
                  icon="cart"
                  label="Web orders"
                  badge={webOrderCounts?.toHandle ?? 0}
                />
              ) : null}
              {SHOW_BUILD_BOUQUET_SIDEBAR_LINK ? (
                <NavItem to="/app/bouquets/build" icon="flower" label="Build bouquet" />
              ) : null}
              {hasRole("OWNER", "MANAGER") ? (
                <NavItem to="/app/orders/build" icon="buildSend" label="Build & send order" />
              ) : null}
              {hasRole("OWNER", "MANAGER") ? (
                <NavItem to="/app/dispatch" icon="send" label="Courier board" />
              ) : null}
            </NavSection>
          ) : null}

          {hasRole("OWNER", "MANAGER") ? (
            <NavSection title="Shopify">
              <NavItem
                to="/app/shopify/products"
                icon="storefront"
                label="Products"
                badge={webOrderCounts?.unlinkedProducts ?? 0}
              />
              <NavItem to="/app/blog-posts" icon="pencil" label="Blog posts" />
              {hasRole("OWNER", "ADMIN") ? (
                <NavItem to="/app/shopify" icon="layers" label="Connection" end />
              ) : null}
            </NavSection>
          ) : null}

          {hasRole("OWNER", "MANAGER", "FLORIST") ? (
            <NavSection title="Stock">
              {/* G-83: /app/inventory once had no entry anywhere and was
                  reachable only by typing the URL. G-94 S4: the shop is picked
                  on the Inventory page itself; `/app/locations/:id` redirects
                  there. */}
              {hasRole("OWNER", "MANAGER") ? (
                <NavItem to="/app/inventory" icon="box" label="Inventory" />
              ) : null}
              <NavItem to="/app/deliveries" icon="inbox" label="Incoming stock" />
              {/* G-131: the gate matches the route's RoleGate exactly. */}
              {hasRole("OWNER", "MANAGER") ? (
                <NavItem to="/app/invoices" icon="list" label="Supplier papers" />
              ) : null}
            </NavSection>
          ) : null}

          <NavSection title="People">
            {hasRole("OWNER", "MANAGER", "FLORIST") ? (
              <NavItem to="/app/schedule" icon="calendar" label="Schedule" />
            ) : null}
            <NavItem to="/app/my/shifts" icon="clock" label="My shifts" />
            {hasRole("OWNER", "MANAGER") ? (
              <NavItem to="/app/users" icon="people" label="Team" />
            ) : null}
            {hasRole("OWNER") ? (
              <NavItem to="/app/couriers" icon="bike" label="Couriers" />
            ) : null}
          </NavSection>

          {hasRole("OWNER", "MANAGER") ? (
            <NavSection title="Insights">
              <NavItem to="/app/finances" icon="banknote" label="Finances" />
              {/* G-57: managers get Analytics too, scoped to their shop server-side. */}
              <NavItem to="/app/analytics" icon="chart" label="Analytics" />
              {SHOW_EXTENDED_BUSINESS_SIDEBAR_NAV ? (
                <NavItem to="/app/sales" icon="receipt" label="Sales records" />
              ) : null}
              {SHOW_EXTENDED_BUSINESS_SIDEBAR_NAV && hasRole("OWNER") ? (
                <NavItem to="/app/insights/yield" icon="sprout" label="Yield insights" />
              ) : null}
              <NavItem to="/app/action-log" icon="clock" label="Action log" />
            </NavSection>
          ) : null}

          {hasRole("OWNER", "MANAGER") ? (
            <NavSection title="Catalog">
              {showCreateItem ? (
                <NavItem to="/app/items" icon="tag" label="Manage items" />
              ) : null}
              {showCreateItem ? (
                <NavItem to="/app/items/drafts" icon="tray" label="Catalog staging" />
              ) : null}
              {SHOW_EXTENDED_BUSINESS_SIDEBAR_NAV ? (
                <NavItem
                  to="/app/bouquets/templates"
                  icon="layers"
                  label="Bouquet templates"
                />
              ) : null}
              {SHOW_EXTENDED_BUSINESS_SIDEBAR_NAV ? (
                <NavItem to="/app/suppliers" icon="truck" label="Suppliers" />
              ) : null}
              {hasRole("OWNER") ? (
                <NavItem to="/app/locations/manage" icon="map" label="Locations" />
              ) : null}
            </NavSection>
          ) : null}

          {/* G-149 (OD-230): owners and admins only. */}
          {hasRole("OWNER", "ADMIN") ? (
            <NavSection title="Settings">
              <NavItem to="/app/ai-control" icon="sparkles" label="AI control" />
            </NavSection>
          ) : null}

          {hasRole("OWNER") ? (
            <NavSection title="Support">
              <NavItem to="/app/telegram" icon="send" label="Telegram" />
              <NavItem to="/app/feedback" icon="list" label="Feature backlog" />
              {/* G-72: beside the backlog because they are the same job seen
                  from two ends — this is what the shops HIT, that is what we
                  decided to DO about it. */}
              <NavItem to="/app/bug-reports" icon="flag" label="Bug reports" />
            </NavSection>
          ) : null}

          <NavSection title="Account">
            <NavItem to="/app/profile" icon="person" label="Profile" />
          </NavSection>
        </nav>
      </aside>

      <main className="app-content">
        {isMobileNav ? (
          <div className="app-mobile-bar">
            <div className="app-mobile-bar-start">
              <button
                type="button"
                className="icon-button app-nav-toggle"
                aria-expanded={mobileNavOpen}
                aria-controls="app-sidebar"
                aria-label="Menu"
                onClick={mobileNavOpen ? closeMobileNav : openMobileNav}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
            <NavLink
              to="/app"
              end
              className={({ isActive }) =>
                isActive ? "app-mobile-bar-brand is-active" : "app-mobile-bar-brand"
              }
              aria-label="Flower World — home"
            >
              <img src="/fw-stacked-rose.svg" alt="Flower World" decoding="async" className="brand-logo-light" />
                <img src="/fw-stacked-ondark.svg" alt="" decoding="async" className="brand-logo-dark" aria-hidden="true" />
            </NavLink>
            <div className="app-mobile-bar-end">
              <NotificationBell />
            </div>
          </div>
        ) : null}

        {!isMobileNav ? <NotificationBell /> : null}
        {notice ? <div className="alert success">{notice}</div> : null}
        <EmailConfirmBanner />

        <Outlet context={outletCtx} />
      </main>
    </div>
  );
}
